import { useInView } from 'react-intersection-observer'
import { useRef } from 'react'
import cn from 'classnames'
import s from './s.module.css'

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  skipInView?: boolean
  height?: number
  id?: string
  isSquare?: boolean
}

const getDimensions = ({
  height,
  width,
  naturalHeight,
  naturalWidth,
  isSquare,
}: {
  width: number
  naturalWidth: number
  naturalHeight: number
  isSquare?: boolean
  height?: number
}) => {
  let calculatedWidth: number | string | undefined
  let calculatedHeight: number | string | undefined

  if (isSquare) {
    const dimension = height ?? width
    calculatedWidth = dimension
    calculatedHeight = dimension
  } else if (!height) {
    const ratio = naturalWidth / width
    calculatedHeight = Number(naturalHeight / ratio).toFixed(2)
  }

  return { width: calculatedWidth, height: calculatedHeight }
}

export default function LazyLoadImage({ isSquare, ...props }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imgContainerRef = useRef<HTMLDivElement | null>(null)
  const onceRef = useRef(false)
  const height = props.height ?? 100

  const { ref, inView } = useInView({
    threshold: 0,
    skip: props?.skipInView,
    initialInView: props?.skipInView,
    rootMargin: '250px 0px',
    root: props.id ? document.querySelector(`[id="${props.id}"]`) : undefined,
  })

  return (
    <div
      ref={el => {
        ref(el)
        containerRef.current = el
      }}
      className={cn(s.wrap, !inView && s.animation)}
      style={{ height }}
    >
      {inView && (
        <div ref={imgContainerRef} className={cn(s.imgContainer, s.animation)}>
          <img
            {...props}
            style={{ visibility: 'hidden' }}
            ref={imgRef}
            className={cn(props.className, { [s.squareImage]: isSquare })}
            onLoad={e => {
              const target = e.target as HTMLImageElement
              if (!onceRef.current) {
                onceRef.current = true

                const dimensions = getDimensions({
                  height: props.height,
                  width: target.width,
                  naturalHeight: target.naturalHeight,
                  naturalWidth: target.naturalWidth,
                  isSquare,
                })

                if (containerRef.current?.style) {
                  if (dimensions.height != null) {
                    containerRef.current.style.height = `${dimensions.height}px`
                  } else {
                    containerRef.current.style.height = 'auto'
                  }

                  if (dimensions.width != null) {
                    containerRef.current.style.width = `${dimensions.width}px`
                  }
                }
              }

              if (imgRef.current?.style) {
                imgRef.current.style.visibility = 'visible'
              }

              if (imgContainerRef.current?.style) {
                imgContainerRef.current.style.background = 'transparent'
                imgContainerRef.current.style.animation = 'none'
              }
            }}
          />
        </div>
      )}
    </div>
  )
}
